import { mainMenuQueries } from '@/api/common/navigation';
import type { MenuConfigResponse } from '@/api/common/navigation/schema';
import defaultMenu from '@/config/navigation/menu.json';
import type { MenuConfig } from '@/config/navigation/types';
import { env } from '@/env/env';
import { useQuery } from '@tanstack/react-query';

const localMenuItems = defaultMenu.Items as MenuConfig[];
const localHomeRedirect = (defaultMenu as { HomeRedirect?: string }).HomeRedirect;
const useLocalMenu = env.VITE_USE_LOCAL_MENU === 'true';

/** Local fallback shaped as API response for use as placeholderData. */
const localPlaceholder: MenuConfigResponse = {
  Items: localMenuItems,
  Version: defaultMenu.Version,
  HomeRedirect: localHomeRedirect,
  LastUpdated: null,
  RecordVersion: null
};

export interface MenuConfigResult {
  menuItems: MenuConfig[];
  homeRedirect?: string;
  isReady: boolean;
  isFromDatabase: boolean;
}

/**
 * Fetches unified menu configuration from the backend API.
 *
 * When VITE_USE_LOCAL_MENU=true: uses local menu.json directly, no API call.
 * When VITE_USE_LOCAL_MENU=false: fetches from API with local menu.json as
 * placeholderData (menu renders immediately). Falls back to local menu.json
 * if API returns 404 or network error.
 */
export const useMenuConfig = (): MenuConfigResult => {
  const { data, isPlaceholderData, isFetching } = useQuery({
    ...mainMenuQueries.config(),
    enabled: !useLocalMenu,
    placeholderData: localPlaceholder
  });

  if (useLocalMenu) {
    return { menuItems: localMenuItems, homeRedirect: localHomeRedirect, isReady: true, isFromDatabase: false };
  }

  const hasDbConfig = data !== undefined && data !== null;
  const menuItems = hasDbConfig ? data.Items : localMenuItems;

  const isReady = !isFetching && !isPlaceholderData;

  let homeRedirect: string | undefined;
  if (isReady && hasDbConfig) {
    homeRedirect = data.HomeRedirect ?? undefined;
  } else if (isReady) {
    homeRedirect = localHomeRedirect;
  }

  return {
    menuItems,
    homeRedirect,
    isReady,
    isFromDatabase: hasDbConfig && !isPlaceholderData
  };
};
